import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Clock, User, Home, Calendar, MessageSquare, Star, X, ChevronRight, Trash2 } from 'lucide-react';
import axios from 'axios';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const CheckoutRequests = () => {
  const account = currentAccount();
  const canApprove = account?.role === 'superadmin';
  const canManageFinanceActions = isFinanceAccount(account);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [depositModal, setDepositModal] = useState(null); // { req }
  const [depositAction, setDepositAction] = useState('');
  const [pengembalianMethod, setPengembalianMethod] = useState('');
  const [deductionForm, setDeductionForm] = useState({ deduction_category: '', deduction_amount: '', deduction_note: '' });

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const response = await axios.get(`${BACKEND_URL}/api/admin/checkout-requests`, config);
      setRequests(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, action, body = {}) => {
    if (action === 'approve' ? !canApprove : !canManageFinanceActions) return;
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const res = await axios.put(`${BACKEND_URL}/api/admin/checkout-requests/${encodeURIComponent(requestId)}/${action}`, body, config);
      setSuccess(res.data.message);
      setDepositModal(null);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || `Gagal ${action === 'approve' ? 'menyetujui' : 'menolak'}`);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (requestId) => {
    if (!canManageFinanceActions) return;
    if (!window.confirm('Hapus riwayat checkout ini secara permanen?')) return;
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      await axios.delete(`${BACKEND_URL}/api/admin/checkout-requests/${encodeURIComponent(requestId)}`, config);
      setSuccess('Riwayat checkout berhasil dihapus');
      setSelectedHistory(null);
      fetchRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menghapus');
      setTimeout(() => setError(''), 3000);
    }
  };

  const openApproveModal = (req) => {
    if (!canApprove) return;
    setDepositAction(req.deposit ? '' : 'tidak_ada');
    setPengembalianMethod('');
    setDeductionForm({ deduction_category: '', deduction_amount: '', deduction_note: '' });
    setDepositModal(req);
  };

  const submitApprove = async () => {
    if (!depositAction) return setError('Pilih penanganan deposit terlebih dahulu');
    if (['dikembalikan', 'dikembalikan_potongan'].includes(depositAction) && !pengembalianMethod) return setError('Pilih metode pengembalian deposit');
    if (['dikembalikan', 'dikembalikan_potongan', 'hangus'].includes(depositAction) && !depositModal.deposit)
      return setError('Transaksi deposit tenant tidak ditemukan');
    if (depositAction === 'dikembalikan_potongan') {
      const amount = Number(deductionForm.deduction_amount || 0);
      if (!['rent_income', 'other_income'].includes(deductionForm.deduction_category)) return setError('Pilih jenis pendapatan');
      if (!Number.isFinite(amount) || amount <= 0) return setError('Nominal potongan harus lebih dari Rp0');
      if (amount > Number(depositModal.deposit.amount)) return setError('Nominal potongan melebihi deposit awal');
      if (!deductionForm.deduction_note.trim()) return setError('Keterangan potongan wajib diisi');
    }
    handleAction(depositModal.created_at, 'approve', {
      deposit_action: depositAction === 'tidak_ada' ? null : depositAction,
      pengembalian_method: ['dikembalikan', 'dikembalikan_potongan'].includes(depositAction) ? pengembalianMethod : null,
      ...(depositAction === 'dikembalikan_potongan' ? deductionForm : {}),
    });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
  const fmtShort = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const fmtCurrency = value => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(value || 0));

  const StarRating = ({ label, value }) => (
    <div className="text-center">
      <p className="text-xs text-isk-muted mb-1">{label}</p>
      <div className="flex justify-center gap-0.5">
        {[1,2,3,4,5].map(s => (
          <Star key={s} className={`w-4 h-4 ${s <= value ? 'text-isk-gold fill-isk-gold' : 'text-gray-200 fill-gray-100'}`} />
        ))}
      </div>
      <p className="text-xs font-bold text-isk-text mt-0.5">{value || '-'}/5</p>
    </div>
  );

  const DetailCard = ({ req, showActions = false }) => {
    const avgRating = req.rating_kebersihan && req.rating_staff && req.rating_keamanan
      ? ((req.rating_kebersihan + req.rating_staff + req.rating_keamanan) / 3).toFixed(1)
      : null;

    return (
      <div className={`rounded-xl border ${req.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : (req.status === 'approved' || req.status === 'executed') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        {/* Header */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={req.status} />
              {avgRating && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-isk-gold/10 text-isk-gold rounded-full text-xs font-medium">
                  <Star className="w-3 h-3 fill-isk-gold" /> {avgRating}
                </span>
              )}
            </div>
            <span className="text-xs text-isk-muted shrink-0">Diajukan: {fmtShort(req.created_at)}</span>
          </div>

          {/* Info utama */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="p-3 rounded-lg bg-white/70">
              <p className="text-xs text-isk-muted mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Tenant</p>
              <p className="font-semibold text-isk-text">{req.tenant_name}</p>
              <p className="text-xs text-isk-muted">{req.tenant_phone}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/70">
              <p className="text-xs text-isk-muted mb-1 flex items-center gap-1"><Home className="w-3 h-3" /> Properti</p>
              <p className="font-semibold text-isk-text">{req.property_name}</p>
              <p className="text-xs text-isk-muted">Kamar {req.room_number}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/70">
              <p className="text-xs text-isk-muted mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Tanggal Checkout</p>
              <p className="font-semibold text-isk-text">{formatDate(req.scheduled_checkout_date || req.requested_date)}</p>
              {req.scheduled_checkout_date && req.status === 'approved' && (
                <p className="text-xs text-blue-500 mt-0.5">Otomatis diproses pada tanggal ini</p>
              )}
              {req.resolved_at && <p className="text-xs text-isk-muted">Diproses: {fmtShort(req.resolved_at)}</p>}
            </div>
          </div>

          {/* Survey Ratings */}
          {(req.rating_kebersihan || req.rating_staff || req.rating_keamanan) && (
            <div className="p-3 rounded-lg bg-white/60 mb-3">
              <p className="text-xs font-semibold text-isk-muted mb-2">Survey Kepuasan</p>
              <div className="grid grid-cols-3 gap-3">
                <StarRating label="Kebersihan" value={req.rating_kebersihan} />
                <StarRating label="Kinerja Staff" value={req.rating_staff} />
                <StarRating label="Keamanan" value={req.rating_keamanan} />
              </div>
            </div>
          )}

          {/* Alasan */}
          {req.reason && (
            <div className="p-3 rounded-lg bg-white/60 mb-3">
              <p className="text-xs text-isk-muted mb-1 flex items-center gap-1 font-semibold"><MessageSquare className="w-3 h-3" /> Alasan Keluar</p>
              <p className="text-sm text-isk-text">{req.reason}</p>
            </div>
          )}

          {/* Testimoni */}
          {req.testimoni && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-3">
              <p className="text-xs text-blue-500 mb-1 font-semibold">Testimoni</p>
              <p className="text-sm text-isk-text italic">"{req.testimoni}"</p>
            </div>
          )}

          {/* Rekening Pengembalian Deposit */}
          {(req.bank_name || req.bank_account || req.bank_holder) && (
            <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 mb-3">
              <p className="text-xs font-semibold text-yellow-700 mb-2">Rekening Pengembalian Deposit</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-isk-muted">Nama Bank</p>
                  <p className="font-semibold text-isk-text">{req.bank_name || '-'}</p>
                </div>
                <div>
                  <p className="text-isk-muted">No. Rekening</p>
                  <p className="font-semibold text-isk-text font-mono">{req.bank_account || '-'}</p>
                </div>
                <div>
                  <p className="text-isk-muted">Nama Penerima</p>
                  <p className="font-semibold text-isk-text">{req.bank_holder || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {['dikembalikan', 'dikembalikan_potongan'].includes(req.deposit_action) && req.deposit_original_amount != null && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 mb-3 text-xs">
              <p className="font-semibold text-blue-700 mb-2">Rincian Pengembalian Deposit</p>
              <div className="grid grid-cols-2 gap-1.5">
                <span className="text-isk-muted">Deposit awal</span><span className="font-semibold text-right">{fmtCurrency(req.deposit_original_amount)}</span>
                {Number(req.deduction_amount || 0) > 0 && <><span className="text-isk-muted">Potongan {req.deduction_category === 'rent_income' ? 'pendapatan sewa' : 'pendapatan lain-lain'}</span><span className="font-semibold text-right">{fmtCurrency(req.deduction_amount)}</span></>}
                <span className="font-semibold text-blue-700 pt-1 border-t">Dikembalikan</span><span className="font-bold text-blue-700 text-right pt-1 border-t">{fmtCurrency(req.deposit_refund_amount)}</span>
              </div>
              {req.deduction_note && <p className="text-isk-muted mt-2">Keterangan: {req.deduction_note}</p>}
            </div>
          )}

          {showActions && (canApprove || canManageFinanceActions) && (
            <div className="flex gap-3 pt-1">
              {canApprove && <Button onClick={() => openApproveModal(req)} className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                <CheckCircle className="w-4 h-4 mr-2" /> Setujui Checkout
              </Button>}
              {canManageFinanceActions && <Button onClick={() => { if (window.confirm('Tolak permintaan checkout ini?')) handleAction(req.created_at, 'reject'); }} variant="outline" className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white rounded-full">
                <XCircle className="w-4 h-4 mr-2" /> Tolak
              </Button>}
            </div>
          )}
        </div>
      </div>
    );
  };

  const StatusBadge = ({ status }) => ({
    pending: <span className="px-3 py-1 rounded-full text-xs font-semibold bg-isk-gold/10 text-isk-gold">Menunggu</span>,
    approved: <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Disetujui</span>,
    executed: <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Disetujui</span>,
    rejected: <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Ditolak</span>,
  }[status] || <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-600">{status}</span>);

  if (loading) return <div className="text-center py-12"><div className="w-10 h-10 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-isk-muted">Memuat data...</p></div>;

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-isk-text">Permintaan Checkout</h2>
        <p className="text-sm text-isk-muted mt-1">Kelola permintaan checkout dari tenant</p>
      </div>

      {success && <div className="p-4 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-isk-teal">{success}</div>}
      {error && <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">{error}</div>}

      {/* Pending */}
      <div className="glass-card rounded-2xl p-6">
        <h3 className="text-lg font-bold text-isk-text mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-isk-gold" /> Menunggu Persetujuan ({pendingRequests.length})
        </h3>
        {pendingRequests.length > 0 ? (
          <div className="space-y-0">
            {pendingRequests.map((req, idx) => (
              <div key={idx}>
                {idx > 0 && <div className="border-t border-gray-200 my-4" />}
                <DetailCard req={req} showActions={true} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-isk-muted py-4">Tidak ada permintaan checkout yang menunggu</p>
        )}
      </div>

      {/* Riwayat — tabel ringkas */}
      <div className="glass-card rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/20 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-isk-teal" />
          <h3 className="text-lg font-bold text-isk-text">Riwayat Checkout ({processedRequests.length})</h3>
        </div>
        {processedRequests.length === 0 ? (
          <p className="text-center text-isk-muted py-8">Belum ada riwayat checkout</p>
        ) : (
          <>
            <div className="hidden sm:grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1fr_auto] gap-3 px-6 py-2.5 bg-white/30 text-xs font-semibold text-isk-muted border-b border-white/20">
              <span>Nama Tenant</span><span>Kamar</span><span>Properti</span><span>Tgl Checkout</span><span>Status</span><span></span>
            </div>
            <div className="divide-y divide-white/10">
              {processedRequests.map((req, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.5fr_1.5fr_1fr_auto] gap-2 sm:gap-3 px-6 py-3 items-center hover:bg-white/20 transition-colors">
                  <div>
                    <p className="font-medium text-isk-text text-sm">{req.tenant_name}</p>
                    <p className="text-xs text-isk-muted sm:hidden">{req.property_name} · Kamar {req.room_number}</p>
                  </div>
                  <p className="hidden sm:block text-sm text-isk-text">{req.room_number}</p>
                  <p className="hidden sm:block text-xs text-isk-muted truncate">{req.property_name}</p>
                  <p className="hidden sm:block text-xs text-isk-muted">{formatDate(req.requested_date)}</p>
                  <div className="hidden sm:block">
                    {req.status === 'approved' || req.status === 'executed'
                      ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">Disetujui</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-medium">Ditolak</span>}
                  </div>
                  <Button size="sm" onClick={() => setSelectedHistory(req)}
                    className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                    Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal penanganan deposit saat approve */}
      {depositModal && canApprove && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[92vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-isk-text text-base">Setujui Checkout</h3>
                <p className="text-xs text-isk-muted mt-0.5">{depositModal.tenant_name} · Kamar {depositModal.room_number}</p>
              </div>
              <button onClick={() => setDepositModal(null)}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>

            <p className="text-xs font-semibold text-isk-text mb-3">Penanganan Deposit</p>
            <div className="space-y-2 mb-5">
              {[
                { value: 'hangus', label: 'Deposit Hangus', desc: 'Deposit berubah menjadi pendapatan lain-lain (other income)', color: 'border-red-400 bg-red-50', active: 'border-red-500 bg-red-50', labelColor: 'text-red-700' },
                { value: 'dikembalikan', label: 'Pengembalian Deposit Penuh', desc: 'Seluruh deposit dikembalikan tanpa potongan', color: 'border-gray-200 bg-gray-50', active: 'border-isk-teal bg-isk-teal/5', labelColor: 'text-isk-teal' },
                { value: 'dikembalikan_potongan', label: 'Pengembalian dengan Potongan', desc: 'Satu potongan manual dicatat sebagai pendapatan sewa atau pendapatan lain-lain', color: 'border-gray-200 bg-gray-50', active: 'border-blue-500 bg-blue-50', labelColor: 'text-blue-700' },
                { value: 'tidak_ada', label: 'Tidak Ada Deposit', desc: 'Tenant tidak memiliki deposit yang perlu diproses', color: 'border-gray-200 bg-gray-50', active: 'border-gray-400 bg-gray-50', labelColor: 'text-isk-muted' },
              ].filter(opt => depositModal.deposit || opt.value === 'tidak_ada').map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${depositAction === opt.value ? opt.active : opt.color}`}>
                  <input type="radio" name="deposit_action" value={opt.value} checked={depositAction === opt.value}
                    onChange={() => setDepositAction(opt.value)} className="mt-0.5 accent-isk-teal" />
                  <div>
                    <p className={`text-xs font-semibold ${depositAction === opt.value ? opt.labelColor : 'text-isk-text'}`}>{opt.label}</p>
                    <p className="text-[11px] text-isk-muted mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {['dikembalikan', 'dikembalikan_potongan'].includes(depositAction) && (
              <div className="mb-4 p-3 rounded-xl border-2 border-isk-teal/40 bg-isk-teal/5 space-y-3">
                <div className="flex justify-between gap-3 text-xs">
                  <span className="text-isk-muted">Deposit awal</span>
                  <span className="font-bold text-isk-text">{depositModal.deposit ? fmtCurrency(depositModal.deposit.amount) : 'Tidak ditemukan'}</span>
                </div>
                {depositAction === 'dikembalikan_potongan' && (
                  <div className="space-y-2">
                    <label className="block text-xs text-isk-muted">Jenis Pendapatan *
                      <select value={deductionForm.deduction_category} onChange={e => setDeductionForm(f => ({ ...f, deduction_category: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-isk-text">
                        <option value="">-- Pilih jenis --</option>
                        <option value="rent_income">Pendapatan Sewa</option>
                        <option value="other_income">Pendapatan Lain-lain</option>
                      </select>
                    </label>
                    <label className="block text-xs text-isk-muted">Nominal Potongan *
                      <input type="number" min="1" value={deductionForm.deduction_amount}
                        onChange={e => setDeductionForm(f => ({ ...f, deduction_amount: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-isk-text" placeholder="0" />
                    </label>
                    <label className="block text-xs text-isk-muted">Keterangan *
                      <input type="text" value={deductionForm.deduction_note}
                        onChange={e => setDeductionForm(f => ({ ...f, deduction_note: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-isk-text" placeholder="Contoh: tambahan sewa 4 hari" />
                    </label>
                    <div className="flex justify-between border-t pt-2 text-xs font-bold text-isk-teal">
                      <span>Deposit dikembalikan</span>
                      <span>{fmtCurrency(Math.max(0, Number(depositModal.deposit?.amount || 0) - Number(deductionForm.deduction_amount || 0)))}</span>
                    </div>
                  </div>
                )}
                <p className="text-xs font-semibold text-isk-teal mb-2">Metode Pengembalian Deposit</p>
                <select
                  value={pengembalianMethod}
                  onChange={e => setPengembalianMethod(e.target.value)}
                  className="w-full rounded-lg border border-isk-teal/30 bg-white text-xs text-isk-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-isk-teal/30"
                >
                  <option value="">-- Pilih metode --</option>
                  <option value="BCA">BCA</option>
                  <option value="GOPAY">GOPAY</option>
                  <option value="DANA">DANA</option>
                </select>
              </div>
            )}

            {error && <p className="text-xs text-red-600 mb-3">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setDepositModal(null)} className="flex-1 py-2 rounded-full border border-gray-200 text-xs text-isk-muted hover:bg-gray-50">Batal</button>
              <button onClick={submitApprove} disabled={!depositAction}
                className="flex-1 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold disabled:opacity-40">
                Konfirmasi & Setujui
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup detail riwayat */}
      {selectedHistory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-bold text-isk-text text-lg">{selectedHistory.tenant_name}</h3>
                <p className="text-xs text-isk-muted">{selectedHistory.property_name} · Kamar {selectedHistory.room_number}</p>
              </div>
              <button onClick={() => setSelectedHistory(null)}><X className="w-5 h-5" /></button>
            </div>
            <DetailCard req={selectedHistory} showActions={false} />
            {canManageFinanceActions && selectedHistory.status === 'rejected' && (
              <div className="mt-4">
                <Button size="sm" variant="outline" onClick={() => handleDelete(selectedHistory.created_at)}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Riwayat
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutRequests;
